export interface ParsedPair {
  ottoSku: string;
  varioNumber: string;
}

export interface OfferImportParse {
  /** Gültige Paare, dedupliziert nach ottoSku (letzte Zeile gewinnt). */
  pairs: ParsedPair[];
  /** Zeilen, die nicht als `OTTOSKU;VARIOSKU` interpretierbar waren (1-basierte Zeilennummer). */
  invalidLines: Array<{ line: number; raw: string }>;
  /** OTTO-SKUs, die mehrfach vorkamen (einmalig gelistet). */
  duplicates: string[];
}

function isHeader(otto: string, vario: string): boolean {
  return otto.toLowerCase() === 'ottosku' && vario.toLowerCase() === 'variosku';
}

/**
 * Parst rohen Import-Text (`OTTOSKU;VARIOSKU`, ein Paar je Zeile) in gültige Paare,
 * ungültige Zeilen und Datei-Duplikate. Rein (kein I/O) → in Vorschau UND Test nutzbar.
 * Split am ERSTEN Semikolon (VARIO-Nummern dürfen selbst `;` enthalten). Optionale
 * Kopfzeile `OTTOSKU;VARIOSKU` wird übersprungen. Dedup nach ottoSku: letzte gewinnt.
 */
export function parseOfferImport(rawText: string): OfferImportParse {
  const lines = rawText.split(/\r?\n/);
  const invalidLines: Array<{ line: number; raw: string }> = [];
  const order: string[] = [];
  const byOtto = new Map<string, string>();
  const seenTwice = new Set<string>();
  let headerConsumed = false;

  for (let i = 0; i < lines.length; i += 1) {
    const raw = lines[i]!;
    const trimmed = raw.trim();
    if (trimmed.length === 0) continue;
    const sep = trimmed.indexOf(';');
    if (sep < 0) {
      invalidLines.push({ line: i + 1, raw });
      continue;
    }
    const ottoSku = trimmed.slice(0, sep).trim();
    const varioNumber = trimmed.slice(sep + 1).trim();
    if (ottoSku.length === 0 || varioNumber.length === 0) {
      invalidLines.push({ line: i + 1, raw });
      continue;
    }
    if (!headerConsumed && isHeader(ottoSku, varioNumber)) {
      headerConsumed = true;
      continue;
    }
    headerConsumed = true;
    if (byOtto.has(ottoSku)) {
      if (!seenTwice.has(ottoSku)) seenTwice.add(ottoSku);
    } else {
      order.push(ottoSku);
    }
    byOtto.set(ottoSku, varioNumber);
  }

  const pairs = order.map((ottoSku) => ({ ottoSku, varioNumber: byOtto.get(ottoSku)! }));
  return { pairs, invalidLines, duplicates: [...seenTwice] };
}
